import React, { useCallback, useEffect, useState } from 'react';
import { hasModule } from '../F22/registry';
import { getRenta, getRentaPagado } from '../../../services/declaracionRenta/f22Service';
import { useAuth } from '../../../context/AuthContext';
import F22FormPage from '../F22/F22FormPage';
import RentaSavedView from './RentaSavedView';

/**
 * Paso 4 del wizard: dado (año, régimen) ya elegidos, decide qué mostrar.
 *
 * Consulta dos cosas independientes entre sí:
 *   - la declaración CALCULADA por el sistema → si existe, se muestra de
 *     inmediato en modo lectura; si no, se muestra el formulario de captura.
 *   - el CERTIFICADO de renta pagada (el F-22 presentado ante el SII) → no
 *     decide qué pantalla se ve, solo con qué cara aparece su botón. Se
 *     consulta acá, una sola vez, para que su estado no se pierda al
 *     recargar la página.
 *
 * Que las dos sean independientes es justamente lo que permite a una empresa
 * que entra al sistema recién en el año X cargar su certificado sin haber
 * calculado nada todavía.
 */
const ModuleRenderer = ({ anioTributario, regimen }) => {
  const { selectedCompany, token } = useAuth();
  const idCompany = selectedCompany?.id_company || selectedCompany?.id;

  const [loading, setLoading] = useState(true);
  const [calculado, setCalculado] = useState(null);
  const [pagado, setPagado] = useState(null);

  const cargar = useCallback(async () => {
    if (!idCompany || !token) return;
    setLoading(true);
    // Ambos endpoints responden 404 cuando no hay nada guardado: es un estado
    // válido, no un error.
    const [resCalculado, resPagado] = await Promise.all([
      getRenta(idCompany, anioTributario, regimen).catch(() => null),
      getRentaPagado(idCompany, anioTributario, regimen).catch(() => null),
    ]);
    setCalculado(resCalculado?.success ? resCalculado.data : null);
    setPagado(resPagado?.success ? resPagado.data : null);
    setLoading(false);
  }, [idCompany, token, anioTributario, regimen]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  if (!hasModule(anioTributario, regimen)) {
    return (
      <div className="f22-loading">
        <p>Función en desarrollo</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="f22-loading">
        <div className="f22-spinner" />
        <p>Buscando declaración guardada...</p>
      </div>
    );
  }

  if (calculado) {
    return (
      <RentaSavedView
        anioTributario={anioTributario}
        regimen={regimen}
        row={calculado}
        pagado={pagado}
        onRecargar={cargar}
      />
    );
  }

  return (
    <F22FormPage
      key={`${anioTributario}-${regimen}`}
      anioTributario={anioTributario}
      regimen={regimen}
      pagado={pagado}
      onCalculado={cargar}
      onCertificadoCargado={cargar}
    />
  );
};

export default ModuleRenderer;
